import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { ShippingOptionType } from '../bff-api/baskets/model';
import { ShippingMethodId } from '../bff-api/baskets/model/ShippingMethodId';
import { useAddProductItems } from '../bff-api/baskets/useAddProductItems';
import { useAddShipment } from '../bff-api/baskets/useAddShipment';
import { useBasket } from '../bff-api/baskets/useBasket';
import { useUpdateShippingAddress } from '../bff-api/baskets/useUpdateShippingAddress';
import { useUpdateShippingAddressForShipment } from '../bff-api/baskets/useUpdateShippingAddressForShipment';
import { useEstimatedDeliveryDate } from '../bff-api/delivery/useEstimatedDeliveryDate';
import { BFFHttpError } from '../bff-api/model/BFFHttpError';
import { getDefaultShipment } from '../bff-api/utils/basket-utils';
import { buildBopisShipment, buildSddShipment } from '../bff-api/utils/shipment-utils';
import { toOrderAddress } from '../bff-api/utils/store-utils';
import { useExtendWarranty } from '../components/extend-warranty/useExtendWarranty';
import { usePDP } from '../contexts/pdp/usePDP';
import { useProductsAddedToBagDrawer } from '../contexts/products-added-to-bag-drawer';
import { eddAddressToOrderAddress } from '../utils/address-utils';
import { buildProductItemForBag, isBopis, isSDD, isShipTo } from '../utils/product-utils';
import { useGTM } from './gtm/useGTM';
import { useBopisStore, useBopisStoreMutation } from './useBopisStore';
import { useSDDAddress } from './useSDDAddress';
import { useUserZipCode } from './useUserZipCode';
import type { Basket, NewProductItem } from '../bff-api/baskets/model';
import type { Product } from '../bff-api/products/model';
import type { Store } from '../bff-api/stores/model';

export interface AddProductToBag {
    product: Product;
    quantity: number;
    shippingOptionType: ShippingOptionType;
    applyExtend?: boolean;
    store?: Store;
}

export const useAddProductToBag = (options?: {
    ignoreInventory?: boolean;
    openDrawer?: boolean;
    concatAddedProducts?: boolean;
}): UseMutationResult<unknown, unknown, AddProductToBag> => {
    const { data: sddAddress } = useSDDAddress();
    const { data: zipCode = '' } = useUserZipCode();
    const addProductItem = useAddProductItems();
    const { data: basket } = useBasket();
    const updateShippingAddress = useUpdateShippingAddress();
    const updateShippingAddressForShipment = useUpdateShippingAddressForShipment();
    const addShipment = useAddShipment();
    const extend = useExtendWarranty();
    const {
        inventoryStore: { currentInventory },
        setAvailableQuantityToAdd,
    } = usePDP();
    const { data: bopisStore } = useBopisStore();
    const bopisStoreMutation = useBopisStoreMutation(true);
    const edd = useEstimatedDeliveryDate(zipCode);
    const { onAddToBag } = useGTM();
    const { onOpenDrawer, setProductsAddedToBag } = useProductsAddedToBagDrawer();

    const onAddProductToBag = useCallback(
        async (product: Product, item: NewProductItem, store?: Store): Promise<void> => {
            let newBasket: Basket;

            if (item.c_isSameDayDelivery) {
                // SDD
                const sddShipment = basket?.shipments?.find(
                    ({ shippingMethod }) =>
                        shippingMethod?.id === ShippingMethodId.SAME_DAY_DELIVERY,
                );

                let shipmentId: string;
                if (sddShipment) {
                    shipmentId = sddShipment.shipmentId!;

                    const sddItem = { ...item, shipmentId };

                    newBasket = await addProductItem.mutateAsync([sddItem]);

                    await updateShippingAddress.mutateAsync({ address: sddAddress! });
                } else {
                    const shipment = buildSddShipment(sddAddress!, store);

                    shipmentId = shipment.shipmentId!;

                    const sddItem = { ...item, shipmentId };

                    await addShipment.mutateAsync(shipment);

                    try {
                        newBasket = await addProductItem.mutateAsync([sddItem]);
                    } catch (e) {
                        // Sometimes, between adding the shipment and adding the item,
                        // the GET basket request removes the shipment because we are
                        // calling a logic to remove empty shipments. So this retry
                        // is a workaround to try adding the shipment and item again.
                        if (e instanceof BFFHttpError && e.status === 404) {
                            await addShipment.mutateAsync(shipment);

                            newBasket = await addProductItem.mutateAsync([sddItem]);
                        } else {
                            throw e;
                        }
                    }
                }
            } else if (item.c_storepickupenabled) {
                // BOPIS
                const bopisShipment = basket?.shipments?.find(
                    ({ shippingMethod }) => shippingMethod?.id === ShippingMethodId.BOPIS,
                );

                let shipmentId: string;
                if (bopisShipment) {
                    shipmentId = bopisShipment.shipmentId!;

                    const bopisItem = { ...item, shipmentId };

                    const baskets = await Promise.all([
                        addProductItem.mutateAsync([bopisItem]),
                        bopisStoreMutation.mutateAsync(bopisStore!),
                    ]);

                    [newBasket] = baskets;

                    await updateShippingAddressForShipment.mutateAsync({
                        shipmentId,
                        address: toOrderAddress(store)!,
                        useAsBilling: false,
                    });
                } else {
                    const shipment = buildBopisShipment(store!);

                    shipmentId = shipment.shipmentId!;

                    const bopisItem = { ...item, shipmentId };

                    await addShipment.mutateAsync(shipment);

                    try {
                        newBasket = await addProductItem.mutateAsync([bopisItem]);
                    } catch (e) {
                        // Sometimes, between adding the shipment and adding the item,
                        // the GET basket request removes the shipment because we are
                        // calling a logic to remove empty shipments. So this retry
                        // is a workaround to try adding the shipment and item again.
                        if (e instanceof BFFHttpError && e.status === 404) {
                            await addShipment.mutateAsync(shipment);

                            newBasket = await addProductItem.mutateAsync([bopisItem]);
                        } else {
                            throw e;
                        }
                    }

                    await bopisStoreMutation.mutateAsync(bopisStore!);
                }
            } else {
                // SHIP TO
                newBasket = await addProductItem.mutateAsync([item]);

                const defaultShipment = getDefaultShipment(newBasket!);
                if (
                    edd.data &&
                    defaultShipment &&
                    defaultShipment.shippingAddress?.postalCode !== edd.data.postalCode
                ) {
                    const address = eddAddressToOrderAddress(edd.data);

                    await updateShippingAddressForShipment.mutateAsync({
                        shipmentId: defaultShipment.shipmentId!,
                        address,
                        useAsBilling: false,
                    });
                }
            }

            product.c_quantityAddedToBag = item.quantity;

            onAddToBag(product, newBasket);

            const itemsAdded = [product];

            if (item.c_extendPlan) {
                const extendItem = extend.buildExtendProductFromProductItem(newBasket, item)!;
                extendItem.c_quantityAddedToBag = item.quantity;

                onAddToBag(extendItem, newBasket);

                itemsAdded.push(extendItem);
            }

            setProductsAddedToBag(itemsAdded, { concatProducts: !!options?.concatAddedProducts });

            if (options?.openDrawer) {
                onOpenDrawer();
            }
        },
        [
            basket,
            addProductItem,
            updateShippingAddress,
            updateShippingAddressForShipment,
            extend,
            onOpenDrawer,
            sddAddress,
            setProductsAddedToBag,
            onAddToBag,
            addShipment,
            bopisStore,
            bopisStoreMutation,
            options,
            edd.data,
        ],
    );

    return useMutation<unknown, unknown, AddProductToBag>({
        mutationFn: async ({
            product,
            quantity,
            shippingOptionType,
            applyExtend,
            store,
        }: AddProductToBag) => {
            const itemFromBag = basket?.productItems?.find(
                ({ productId }) => productId === product.id,
            );

            let remaining = currentInventory.availableToSell;
            if (itemFromBag) {
                let shouldConsiderBasket = false;

                if (shippingOptionType === ShippingOptionType.SHIP_TO && isShipTo(itemFromBag)) {
                    shouldConsiderBasket = true;
                } else if (
                    shippingOptionType === ShippingOptionType.SDD &&
                    isSDD(itemFromBag) &&
                    basket?.c_goLocalStoreID === store?.id
                ) {
                    shouldConsiderBasket = true;
                } else if (
                    shippingOptionType === ShippingOptionType.BOPIS &&
                    isBopis(itemFromBag) &&
                    basket?.c_bopisStoreID === store?.id
                ) {
                    shouldConsiderBasket = true;
                }

                if (shouldConsiderBasket && itemFromBag.quantity) {
                    remaining -= itemFromBag.quantity;
                }
            }

            if (!options?.ignoreInventory && quantity > remaining) {
                setAvailableQuantityToAdd(Math.max(0, remaining));

                return Promise.resolve();
            }

            setAvailableQuantityToAdd(null);

            const item = buildProductItemForBag(
                product,
                quantity,
                shippingOptionType,
                store?.id,
                edd.data?.edd,
            );

            if (
                applyExtend &&
                extend.isPDPEnabled &&
                shippingOptionType === ShippingOptionType.SHIP_TO
            ) {
                try {
                    extend.addPlan(item);

                    if (extend.isPDPUpSellEnabled && !item.c_extendPlan) {
                        return await new Promise<void>((resolve) => {
                            extend.openModal(product.id, async (plan) => {
                                extend.addPlan(item, plan);

                                await onAddProductToBag(product, item);
                                resolve();
                            });
                        });
                    }
                } catch (e) {
                    console.error(e);
                    // Ignore Extend error to continue to add to bag.
                }
            }

            return onAddProductToBag(product, item, store);
        },
    });
};
